import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Input, Button, DatePicker, Rate, Select, Radio, Empty, Segmented, Popconfirm, Image, Tooltip, Modal, Progress, message,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import {
    Phone, PhoneCall, MessageSquare, User, PenTool, CalendarClock, CalendarCheck, Star, Send, Smartphone, AlertTriangle,
    ImagePlus, Trash2, CheckCircle2, Receipt, PhoneOff, Pencil, ShieldCheck, Download, RotateCcw, ArrowDownLeft, ArrowUpRight,
    ClipboardCheck, Stethoscope,
} from 'lucide-react';
import {
    APPOINTMENT_STATUS, CARE_EDIT_MINUTES, CARE_RESULT, CHANNEL_LABEL, CHECK_LEVEL, COMPLAINT_CATEGORY, CONTACT_CHANNEL, CSAT_LOW, FAIL_REASONS,
    KIND_LABEL, NPS_DETRACTOR, OUTCOME_LABEL, PHOTO_ANGLE_LABEL, PHOTO_CONSENT_LABEL, PHOTO_MILESTONE, PHOTO_TAG_LABEL, SEVERITY, STORAGE_LIMIT,
    SURVEY_MAX_ATTEMPTS, appointmentsOf, assigneeOf, callCenterApi, callKind, complaintMetaOf, fmtDuration, formatMoney, hasRecording,
    fmtBytes, isMedicalPhoto, isMedicalTask, isComplaintTask, milestoneFor, missingNote, npsOf, storageBytes, surveyMin, surveyStateOf, taskText, timelineOf,
    getCallCenter, upcomingCallbacks, useCallCenter, visiblePhotos,
    type CareChannel, type CareEntry, type CareResult, type ComplaintCategory, type ContactChannel, type Customer, type Direction,
    type Photo, type PhotoAngle, type PhotoConsentLevel, type PhotoMilestone, type PhotoTag, type Severity, type Survey, type TimelineItem, type TimelineKind,
} from '../../CustomerDemo/callCenterStore';
import { meName, softphone } from '../../CustomerDemo/softphone';
import { RecordingPlayer } from '../../CustomerDemo/components/CallWidgets';
import { AftercareCheckButton, AssigneeSelect, RescheduleTaskButton, TaskBadges, TaskDoneButton } from '../../CustomerDemo/components/TaskWidgets';
import { fileToDataUrl } from '../../CustomerDemo/imageUtil';

export const MessengerIcon = ({ className = "" }: { className?: string; size?: number | string }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C6.477 2 2 6.145 2 11.259c0 2.923 1.488 5.485 3.791 7.159v3.473a.852.852 0 001.353.687l3.228-2.152c.523.15 1.066.233 1.628.233 5.523 0 10-4.145 10-9.259S17.523 2 12 2zm1.096 12.396l-2.483-2.65-4.832 2.65 5.312-5.65 2.483 2.65 4.832-2.65-5.312 5.65z" /></svg>
);

const CHANNEL_STYLE: Record<CareChannel, { dot: string; border: string; title: string; Icon: React.ComponentType<{ size?: number | string; className?: string }> }> = {
    care: { dot: 'bg-teal-500', border: 'border-teal-200', title: 'text-teal-700', Icon: PenTool },
    zalo: { dot: 'bg-blue-500', border: 'border-blue-200', title: 'text-blue-600', Icon: MessageSquare },
    messenger: { dot: 'bg-purple-500', border: 'border-purple-200', title: 'text-purple-600', Icon: MessengerIcon },
    sms: { dot: 'bg-amber-500', border: 'border-amber-200', title: 'text-amber-700', Icon: Smartphone },
    note: { dot: 'bg-gray-500', border: 'border-gray-200', title: 'text-gray-700', Icon: PenTool },
};
const isMessaging = (c: CareChannel) => c === 'zalo' || c === 'messenger' || c === 'sms';
const chName = (c: CareChannel) => (c === 'care' ? 'Trực tiếp / chung' : CHANNEL_LABEL[c].replace('Nhắn ', '').replace('Gửi ', ''));

const WEEKDAY = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
/** Tiêu đề nhóm theo ngày: Hôm nay / Hôm qua / Thứ x, dd/MM/yyyy */
const dayLabel = (t: number) => {
    const d = dayjs().startOf('day').diff(dayjs(t).startOf('day'), 'day');
    return d === 0 ? 'Hôm nay' : d === 1 ? 'Hôm qua' : `${WEEKDAY[dayjs(t).day()]}, ${dayjs(t).format('DD/MM/YYYY')}`;
};
export function groupByDay<T>(items: T[], at: (x: T) => number): { day: string; items: T[] }[] {
    const out: { day: string; items: T[] }[] = [];
    items.forEach(x => {
        const day = dayLabel(at(x));
        const last = out[out.length - 1];
        if (last && last.day === day) last.items.push(x); else out.push({ day, items: [x] });
    });
    return out;
}
export const DayHeader = ({ label }: { label: string }) => (
    <div className="relative z-20 py-1.5 mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
        <span className="bg-white dark:bg-[#141414] pr-2">{label}</span>
    </div>
);
const Chip = ({ text, onClick }: { text: string; onClick: () => void }) => (
    <button type="button" onClick={onClick} className="text-xs px-2 py-1 rounded-full border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[#0d9488] cursor-pointer">{text}</button>
);

// ============================ Việc đang mở ============================

/** Việc gọi lại / khiếu nại / bất thường / hỏi thăm / khảo sát đang mở — ưu tiên bất thường, khiếu nại lên đầu */
function OpenTasks({ customer, onSurvey }: { customer: Customer; onSurvey: (appointmentId?: string) => void }) {
    const s = useCallCenter();
    const rank = (n?: string) => (isMedicalTask({ note: n }) ? 0 : isComplaintTask({ note: n }) ? 1 : 2);
    const tasks = [...upcomingCallbacks(s, customer.phones)].sort((a, b) => rank(a.note) - rank(b.note) || a.dueAt - b.dueAt);
    if (!tasks.length) return null;
    const now = Date.now();
    return (
        <div className="mb-5 border border-amber-200 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-900 rounded-xl p-3 space-y-2">
            <div className="text-sm font-semibold text-amber-700 flex items-center gap-1.5"><CalendarClock size={15} /> Việc đang mở ({tasks.length})</div>
            {tasks.map(cb => {
                const overdue = cb.dueAt < now;
                const meta = complaintMetaOf(cb);
                return (
                    <div key={cb.id} className={`rounded-lg border px-3 py-2 bg-white/80 dark:bg-gray-900/60 ${isMedicalTask(cb) ? 'border-red-300' : meta ? 'border-rose-200' : 'border-amber-100 dark:border-gray-700'}`}>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                            <span className={`shrink-0 font-medium tabular-nums ${overdue ? 'text-rose-600' : 'text-gray-700 dark:text-gray-200'}`}>{dayjs(cb.dueAt).format('HH:mm DD/MM')}{overdue ? ' · quá hạn' : ''}</span>
                            <TaskBadges cb={cb} />
                            <span className="flex-1 min-w-[160px] truncate text-gray-600 dark:text-gray-300" title={taskText(cb)}>{taskText(cb) || '—'}</span>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-1 mt-1">
                            <span className="flex items-center gap-1 text-xs text-gray-400"><User size={12} /> <AssigneeSelect cb={cb} /></span>
                            <span className="flex items-center flex-wrap">
                                {!customer.doNotCall && <Button size="small" type="link" icon={<Phone size={12} />} onClick={() => softphone.dial(cb.phone, { callbackId: cb.id })}>Gọi</Button>}
                                <RescheduleTaskButton cb={cb} buttonProps={{ type: 'link' }} />
                                <TaskDoneButton cb={cb} onSurvey={onSurvey} buttonProps={{ type: 'link' }} />
                            </span>
                        </div>
                        {meta && overdue && meta.level < 2 && <div className="text-[11px] text-rose-600 mt-0.5">Quá hạn xử lý ({SEVERITY[meta.severity].slaHours} giờ) — nên chuyển quản lý.</div>}
                    </div>
                );
            })}
        </div>
    );
}

// ============================ Hành trình ============================

/** Nội dung một mục chăm sóc: sửa trong 15 phút (người viết), quá hạn thì đính chính */
function CareEntryBody({ e }: { e: CareEntry }) {
    const me = meName();
    const [mode, setMode] = useState<'view' | 'edit' | 'fix'>('view');
    const [text, setText] = useState('');
    const canEdit = e.author === me && Date.now() - e.at <= CARE_EDIT_MINUTES * 60000;
    const submit = () => {
        const err = mode === 'edit' ? callCenterApi.updateCare(e.id, text, me) : callCenterApi.addCareCorrection(e.id, text, me);
        if (err) { message.warning(err); return; }
        message.success(mode === 'edit' ? 'Đã sửa' : 'Đã thêm đính chính');
        setMode('view');
    };
    return (
        <div>
            {e.check && (
                <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${CHECK_LEVEL[e.check.level].badge}`}>{CHECK_LEVEL[e.check.level].label}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{CONTACT_CHANNEL[e.check.channel]}</span>
                    {e.check.symptoms.map(x => <span key={x} className="text-[10px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-600">{x}</span>)}
                </div>
            )}
            {mode === 'edit' ? null : <div className="text-gray-700 dark:text-gray-200 text-sm leading-relaxed whitespace-pre-line">{e.content}</div>}
            {e.editedAt && mode !== 'edit' && <div className="text-[11px] text-gray-400 mt-0.5">Đã sửa lúc {dayjs(e.editedAt).format('HH:mm')}</div>}
            {e.corrections?.map((c, i) => (
                <div key={i} className="mt-2 text-xs border-l-2 border-amber-300 pl-2 text-gray-600 dark:text-gray-300"><b className="text-amber-700">Đính chính</b> · {c.by} · {dayjs(c.at).format('HH:mm DD/MM')}: {c.text}</div>
            ))}
            {mode !== 'view' ? (
                <div className="mt-2 space-y-2">
                    <Input.TextArea autoFocus value={text} onChange={x => setText(x.target.value)} autoSize={{ minRows: 2, maxRows: 6 }} placeholder={mode === 'fix' ? 'Nội dung đính chính (nội dung gốc được giữ nguyên)' : ''} />
                    <div className="flex justify-end gap-2"><Button size="small" onClick={() => setMode('view')}>Huỷ</Button><Button size="small" type="primary" disabled={!text.trim()} onClick={submit}>Lưu</Button></div>
                </div>
            ) : (
                <div className="mt-1.5 flex gap-3 text-xs">
                    {canEdit
                        ? <button type="button" className="text-gray-400 hover:text-[#0d9488] cursor-pointer flex items-center gap-1" onClick={() => { setText(e.content); setMode('edit'); }}><Pencil size={11} /> Sửa (trong {CARE_EDIT_MINUTES} phút)</button>
                        : <button type="button" className="text-gray-400 hover:text-amber-600 cursor-pointer flex items-center gap-1" onClick={() => { setText(''); setMode('fix'); }}><Pencil size={11} /> Đính chính</button>}
                </div>
            )}
        </div>
    );
}

function TimelineEntry({ item }: { item: TimelineItem }) {
    const s = useCallCenter();
    const time = dayjs(item.at).format('HH:mm');
    const wrap = (dot: string, icon: React.ReactNode, border: string, body: React.ReactNode) => (
        <div className="relative pl-14 mb-5">
            <div className={`absolute left-1 top-0 w-10 h-10 ${dot} rounded-full flex items-center justify-center text-white z-10 border-[3px] border-white dark:border-[#141414] shadow-sm`}>{icon}</div>
            <div className={`bg-white dark:bg-gray-900 border ${border} rounded-xl p-4 shadow-sm`}>{body}</div>
        </div>
    );
    const head = (title: React.ReactNode, who: string, extra?: React.ReactNode) => (
        <div className="flex flex-wrap items-center justify-between mb-2 gap-2">
            <h4 className="font-medium text-[15px] flex items-center gap-2 flex-wrap">{title}{extra}</h4>
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500"><User size={12} /> {who} · {time}</span>
        </div>
    );

    if (item.kind === 'call') {
        const c = item.call;
        const ok = c.outcome === 'answered';
        return wrap(ok ? 'bg-emerald-500' : 'bg-rose-500', <Phone size={14} />, ok ? 'border-emerald-200' : 'border-rose-200', <>
            {head(<span className={ok ? 'text-emerald-700' : 'text-rose-600'}>{KIND_LABEL[callKind(c)]} · {OUTCOME_LABEL[c.outcome]}{c.duration ? ` · ${fmtDuration(c.duration)}` : ''}</span>, c.agent,
                <>{c.manual && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">Ngoài tổng đài</span>}
                    {missingNote(c) && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Thiếu nội dung</span>}</>)}
            {c.failReason && <div className="text-xs text-rose-500 mb-1.5">Lý do: {FAIL_REASONS.find(r => r.id === c.failReason)?.text}</div>}
            {c.note && <div className="text-gray-700 dark:text-gray-200 text-sm leading-relaxed whitespace-pre-line mb-2">{c.note}</div>}
            {hasRecording(c) && <RecordingPlayer call={c} compact />}
        </>);
    }
    if (item.kind === 'care') {
        const e = item.entry;
        const st = CHANNEL_STYLE[e.channel];
        const inbound = e.direction === 'in';
        const title = e.check ? 'Hỏi thăm sau dịch vụ' : isMessaging(e.channel) ? `${inbound ? 'Khách nhắn' : 'Nhắn khách'} qua ${chName(e.channel)}` : CHANNEL_LABEL[e.channel];
        return wrap(e.check?.level === 'abnormal' ? 'bg-red-600' : st.dot, e.check ? <ClipboardCheck size={15} /> : <st.Icon size={15} className="w-4 h-4" />,
            e.result === 'complaint' || e.check?.level === 'abnormal' ? 'border-rose-300' : st.border, <>
            {head(<span className={`${st.title} flex items-center gap-1`}>{isMessaging(e.channel) && (inbound ? <ArrowDownLeft size={13} /> : <ArrowUpRight size={13} />)}{title}</span>, inbound ? `${e.author} ghi nhận` : e.author,
                e.result && !e.check && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${CARE_RESULT[e.result].badge}`}>{CARE_RESULT[e.result].label}</span>)}
            <CareEntryBody e={e} />
        </>);
    }
    if (item.kind === 'appointment') {
        const a = item.appointment;
        const created = item.event === 'Đặt lịch hẹn';
        return wrap('bg-indigo-500', <CalendarCheck size={15} />, 'border-indigo-200', <>
            {head(<span className="text-indigo-600">{created ? 'Đặt lịch hẹn' : `Lịch hẹn · ${item.event}`}</span>, item.by,
                created && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${APPOINTMENT_STATUS[a.status].badge}`}>Hiện tại: {APPOINTMENT_STATUS[a.status].label}</span>)}
            <div className="text-sm text-gray-700 dark:text-gray-200">{a.service} · {a.branch}{a.practitioner ? ` · ${a.practitioner}` : ''}{created ? ` · hẹn ${dayjs(a.at).format('HH:mm DD/MM/YYYY')}` : ''}{created && a.note ? ` — ${a.note}` : ''}</div>
        </>);
    }
    if (item.kind === 'callback') {
        const cb = item.callback;
        const meta = complaintMetaOf(cb);
        const medical = isMedicalTask(cb);
        const title = medical ? 'Đã xử lý bất thường' : meta ? 'Đã xử lý khiếu nại' : 'Hoàn thành việc';
        return wrap(medical ? 'bg-red-600' : meta ? 'bg-rose-500' : 'bg-amber-500', medical ? <Stethoscope size={15} /> : <CheckCircle2 size={15} />, meta || medical ? 'border-rose-200' : 'border-amber-200', <>
            {head(<span className={meta || medical ? 'text-rose-600' : 'text-amber-700'}>{title}</span>, cb.doneBy || cb.by,
                <>{meta && cb.satisfied !== undefined && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${cb.satisfied ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>{cb.satisfied ? 'Khách hài lòng' : 'Khách chưa hài lòng → mở lại'}</span>}
                    {meta && <span className={`text-[10px] px-1.5 py-0.5 rounded ${SEVERITY[meta.severity].badge}`}>{COMPLAINT_CATEGORY[meta.category]}</span>}</>)}
            <div className="text-xs text-gray-500 mb-1">{taskText(cb) || '—'} · hạn {dayjs(cb.dueAt).format('HH:mm DD/MM')}{cb.doneAt! > cb.dueAt ? <span className="text-rose-500"> · trễ hạn</span> : ''}{cb.escalated ? ' · đã chuyển quản lý' : ''}</div>
            <div className="text-sm text-gray-700 dark:text-gray-200">{cb.result || <i className="text-gray-400">Không ghi kết quả</i>}</div>
        </>);
    }
    if (item.kind === 'invoice') {
        const neg = (item.amount ?? 0) < 0;
        return wrap(neg ? 'bg-purple-500' : 'bg-emerald-600', <Receipt size={15} />, neg ? 'border-purple-200' : 'border-emerald-200', <>
            {head(<span className={neg ? 'text-purple-600' : 'text-emerald-700'}>{item.event}</span>, item.by)}
            <div className="text-sm text-gray-700 dark:text-gray-200">
                {item.invoice.items.map(x => x.name).join(', ')}{item.amount !== undefined ? <> · <b className="tabular-nums">{neg ? '−' : ''}{formatMoney(Math.abs(item.amount))}</b></> : ''}
            </div>
        </>);
    }
    if (item.kind === 'dnc') {
        const e = item.entry;
        return wrap('bg-red-600', <PhoneOff size={15} />, 'border-red-200', <>
            {head(<span className="text-red-600">{e.to.startsWith('Có') ? 'Chặn gọi khách' : 'Gỡ chặn gọi'}</span>, e.by)}
            <div className="text-sm text-gray-700 dark:text-gray-200">{e.to.replace(/^Có — /, 'Lý do: ')}</div>
        </>);
    }
    const sv = item.survey;
    if (sv.status && sv.status !== 'done') {
        return wrap('bg-gray-400', <Star size={15} />, 'border-gray-200', <>
            {head(<span className="text-gray-600">Khảo sát · {sv.status === 'declined' ? 'Khách từ chối' : 'Không liên lạc được'}</span>, sv.by)}
            {sv.comment && <div className="text-sm text-gray-600 dark:text-gray-300">{sv.comment}</div>}
        </>);
    }
    const low = surveyMin(sv) <= CSAT_LOW;
    const ap = s.appointments.find(a => a.id === sv.appointmentId);
    return wrap(low ? 'bg-rose-400' : 'bg-amber-400', <Star size={15} />, low ? 'border-rose-200' : 'border-amber-200', <>
        {head(<span className={low ? 'text-rose-600' : 'text-amber-600'}>Khảo sát hài lòng · {((sv.service + sv.attitude) / 2).toFixed(1)}/5{typeof sv.nps === 'number' ? ` · NPS ${sv.nps}` : ''}</span>, sv.by)}
        <div className="text-sm text-gray-600 dark:text-gray-300">
            Dịch vụ {sv.service}★ · Thái độ {sv.attitude}★{ap?.practitioner && sv.practitionerScore ? ` · ${ap.practitioner} ${sv.practitionerScore}★` : ''}{sv.comment ? ` — ${sv.comment}` : ''}
        </div>
    </>);
}

type TimelineFilter = 'all' | 'call' | 'care' | 'appointment' | 'survey' | 'other';
/** Việc đã đóng, hoá đơn, chặn gọi gom vào nhóm "Khác" */
const filterOf = (k: TimelineKind): TimelineFilter => (k === 'callback' || k === 'invoice' || k === 'dnc' ? 'other' : k);

/** Mẫu nội dung nhanh theo giai đoạn chăm sóc — {ten}, {dichvu}, {lich} được thay tự động */
const CARE_TEMPLATES: { label: string; text: string }[] = [
    { label: 'Nhắc lịch hẹn', text: 'Chào {ten}, spa xin nhắc lịch hẹn {dichvu} lúc {lich}. Mình xác nhận giúp spa nhé, cần đổi giờ cứ báo spa ạ.' },
    { label: 'Hỏi thăm sau DV', text: 'Chào {ten}, spa hỏi thăm tình trạng sau khi làm {dichvu}. Vùng điều trị hiện có sưng, đau hay khó chịu gì không ạ?' },
    { label: 'Hướng dẫn chăm sóc', text: 'Spa gửi {ten} hướng dẫn chăm sóc sau {dichvu}: giữ vệ sinh, tránh nắng, không tự ý bôi thuốc lạ. Có gì bất thường báo spa ngay nhé.' },
    { label: 'Mời tái khám', text: 'Chào {ten}, đã đến thời điểm tái khám sau {dichvu}. Spa xin phép đặt lịch giúp mình, mình tiện ngày nào ạ?' },
    { label: 'Gửi báo giá', text: 'Spa gửi {ten} báo giá {dichvu} và ưu đãi tháng này. Mình cần tư vấn thêm cứ nhắn spa nhé.' },
];

function CareJourney({ customer, onCall, onSurvey }: { customer: Customer; onCall: () => void; onSurvey: (appointmentId?: string) => void }) {
    const s = useCallCenter();
    const [channel, setChannel] = useState<CareChannel>('care');
    const [direction, setDirection] = useState<Direction>('out');
    // Mặc định "Thông tin chung" — tránh lưu nhanh thành "Khách hài lòng" làm sai thống kê
    const [result, setResult] = useState<CareResult>('info');
    const [category, setCategory] = useState<ComplaintCategory>('result');
    const [severity, setSeverity] = useState<Severity>('medium');
    const [complaintAppt, setComplaintAppt] = useState<string>();
    const [text, setText] = useState('');
    const [next, setNext] = useState<Dayjs | null>(null);
    const [filter, setFilter] = useState<TimelineFilter>('all');
    const inputRef = useRef<any>(null);
    const timeline = timelineOf(s, customer.phones, customer.id);
    const appts = appointmentsOf(s, customer.id);
    const doneAppts = appts.filter(a => a.status === 'done');
    const counts = useMemo(() => {
        const c: Record<TimelineFilter, number> = { all: timeline.length, call: 0, care: 0, appointment: 0, survey: 0, other: 0 };
        timeline.forEach(t => { c[filterOf(t.kind)]++; });
        return c;
    }, [timeline]);
    const shown = filter === 'all' ? timeline : timeline.filter(t => filterOf(t.kind) === filter);
    const messaging = isMessaging(channel);
    // Khách đang chặn liên hệ: chỉ nhắn ra khi khách đã đồng ý nhận tin
    const needConsent = messaging && direction === 'out' && !!customer.doNotCall && !customer.messageConsent;

    const fillTemplate = (t: string) => {
        const upcoming = appts.filter(a => (a.status === 'new' || a.status === 'confirmed') && a.at > Date.now()).sort((a, b) => a.at - b.at)[0];
        const svc = upcoming?.service || doneAppts[0]?.service || customer.service || 'dịch vụ';
        setText(t.replace('{ten}', customer.name).replace(/\{dichvu\}/g, svc).replace('{lich}', upcoming ? dayjs(upcoming.at).format('HH:mm DD/MM') : '(chưa có lịch)'));
        inputRef.current?.focus();
    };

    const save = () => {
        if (!text.trim()) { message.warning('Nhập nội dung chăm sóc'); return; }
        if (result === 'follow' && !next) { message.warning('Khách "Cần theo dõi" phải có lịch chăm sóc tiếp'); return; }
        if (next && next.isBefore(dayjs())) { message.warning('Lịch chăm sóc tiếp phải ở tương lai'); return; }
        const err = callCenterApi.addCare(customer.phones[0], channel, text.trim(), meName(), result, {
            direction: messaging ? direction : undefined,
            complaint: result === 'complaint' ? { category, severity, appointmentId: complaintAppt } : undefined,
        });
        if (err) { message.warning(err); return; }
        // Khiếu nại đã tự tạo việc xử lý theo SLA → không tạo thêm hẹn để tránh trùng việc
        if (next && result !== 'complaint') callCenterApi.addCallback(customer.phones[0], next.valueOf(), `Chăm sóc tiếp: ${text.trim().slice(0, 60)}`, meName());
        message.success(result === 'complaint'
            ? `Đã ghi nhận khiếu nại · việc xử lý hạn ${dayjs().add(SEVERITY[severity].slaHours, 'hour').format('HH:mm')}`
            : next ? 'Đã lưu chăm sóc và hẹn chăm sóc tiếp' : 'Đã lưu chăm sóc');
        setText(''); setNext(null); setResult('info'); setComplaintAppt(undefined); setSeverity('medium');
    };

    const pick = (c: CareChannel) => { setChannel(c); setDirection('out'); inputRef.current?.focus(); };
    const tile = 'rounded-xl py-3 flex flex-col items-center justify-center gap-1.5 border cursor-pointer shadow-sm group';

    return (
        <div className="flex-1 flex flex-col min-h-0">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5 shrink-0">
                <button type="button" onClick={onCall} disabled={!!customer.doNotCall} title={customer.doNotCall ? 'Khách đang bị chặn gọi' : undefined}
                    className={`${tile} bg-green-50/60 text-[#16a34a] hover:bg-green-50 border-green-200 disabled:opacity-40 disabled:cursor-not-allowed`}>
                    <PhoneCall className="w-6 h-6 group-hover:scale-110 transition-transform" />
                    <span className="text-[13px] font-medium">Gọi điện</span>
                </button>
                <button type="button" onClick={() => pick('zalo')} title="Ghi nhận nội dung đã nhắn / khách nhắn qua Zalo"
                    className={`${tile} ${channel === 'zalo' ? 'bg-blue-500 text-white border-blue-500' : 'bg-blue-50/60 text-[#3b82f6] border-blue-200 hover:bg-blue-50'}`}>
                    <MessageSquare className="w-6 h-6 group-hover:scale-110 transition-transform" />
                    <span className="text-[13px] font-medium">Ghi nhận Zalo</span>
                </button>
                <button type="button" onClick={() => pick('messenger')} title="Ghi nhận nội dung đã nhắn / khách nhắn qua Messenger"
                    className={`${tile} ${channel === 'messenger' ? 'bg-purple-500 text-white border-purple-500' : 'bg-purple-50/60 text-[#8b5cf6] border-purple-200 hover:bg-purple-50'}`}>
                    <MessengerIcon className="w-6 h-6 group-hover:scale-110 transition-transform" />
                    <span className="text-[13px] font-medium">Ghi nhận Messenger</span>
                </button>
                <AftercareCheckButton customer={customer}
                    buttonProps={{ size: 'middle', icon: <ClipboardCheck className="w-6 h-6" />, disabled: !doneAppts.length, title: doneAppts.length ? 'Phiếu hỏi thăm sau dịch vụ' : 'Khách chưa làm dịch vụ', className: `${tile} !h-auto !bg-teal-50/60 !text-teal-700 !border-teal-200 [&_.ant-btn-icon]:!m-0` }}>
                    <span className="text-[13px] font-medium">Hỏi thăm sau DV</span>
                </AftercareCheckButton>
            </div>

            <OpenTasks customer={customer} onSurvey={onSurvey} />

            <div className="border border-gray-200 dark:border-gray-700 rounded-2xl p-4 mb-6 shadow-sm shrink-0 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-gray-600 dark:text-gray-300 font-semibold text-sm mr-1">Kênh</span>
                    {(['care', 'zalo', 'messenger', 'sms'] as CareChannel[]).map(c => (
                        <button key={c} type="button" onClick={() => pick(c)}
                            className={`px-2.5 py-1 rounded-full text-xs border cursor-pointer ${channel === c ? 'bg-[#0d9488] text-white border-[#0d9488]' : 'border-gray-200 text-gray-600 hover:border-[#0d9488]'}`}>
                            {chName(c)}
                        </button>
                    ))}
                    {messaging && (
                        <Radio.Group size="small" className="ml-auto" optionType="button" buttonStyle="solid" value={direction} onChange={e => setDirection(e.target.value)}
                            options={[{ value: 'out', label: 'Mình gửi' }, { value: 'in', label: 'Khách nhắn đến' }]} />
                    )}
                </div>
                {needConsent && (
                    <div className="text-xs text-rose-700 bg-rose-50 dark:bg-rose-950/30 border border-rose-100 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2">
                        <AlertTriangle size={13} /> <span className="flex-1">Khách đang chặn liên hệ ({customer.doNotCall!.reason}). Chỉ nhắn tin khi khách đồng ý nhận tin.</span>
                        <Popconfirm title="Xác nhận khách đã đồng ý nhận tin chăm sóc?" description="Sự đồng ý được ghi vào Lịch sử cập nhật." okText="Đã đồng ý" cancelText="Huỷ"
                            onConfirm={() => { callCenterApi.setMessageConsent(customer.id, true, meName()); message.success('Đã ghi nhận khách đồng ý nhận tin'); }}>
                            <Button size="small">Khách đã đồng ý</Button>
                        </Popconfirm>
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-gray-400 mr-1">Mẫu nhanh:</span>
                    {CARE_TEMPLATES.map(t => <Chip key={t.label} text={t.label} onClick={() => fillTemplate(t.text)} />)}
                </div>
                <Input.TextArea ref={inputRef} value={text} onChange={e => setText(e.target.value)} autoSize={{ minRows: 3, maxRows: 8 }}
                    placeholder={channel === 'care' ? 'Nội dung trao đổi (VD: Khách báo sưng nhiều vùng mắt...)' : direction === 'in' ? `Khách nhắn gì qua ${chName(channel)}...` : `Nội dung đã gửi qua ${chName(channel)}...`} />
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-gray-600 dark:text-gray-300 font-semibold text-sm mr-1">Kết quả</span>
                    <Segmented size="small" value={result} onChange={v => { setResult(v as CareResult); if (v === 'complaint') setNext(null); }}
                        options={(Object.keys(CARE_RESULT) as CareResult[]).map(k => ({ value: k, label: CARE_RESULT[k].label }))} />
                </div>
                {result === 'complaint' && (
                    <div className="bg-rose-50/70 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900 rounded-lg p-3 space-y-2">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="text-gray-600 dark:text-gray-300 w-20">Phân loại</span>
                            <Select size="small" className="min-w-[180px]" value={category} onChange={setCategory} options={(Object.keys(COMPLAINT_CATEGORY) as ComplaintCategory[]).map(k => ({ value: k, label: COMPLAINT_CATEGORY[k] }))} />
                            <span className="text-gray-600 dark:text-gray-300 ml-2">Mức độ</span>
                            <Segmented size="small" value={severity} onChange={v => setSeverity(v as Severity)} options={(Object.keys(SEVERITY) as Severity[]).map(k => ({ value: k, label: SEVERITY[k].label }))} />
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="text-gray-600 dark:text-gray-300 w-20">Lần DV</span>
                            <Select size="small" allowClear className="min-w-[240px]" value={complaintAppt} onChange={setComplaintAppt} placeholder="Gắn với lần làm dịch vụ (nếu có)"
                                options={appts.filter(a => a.status !== 'cancelled').map(a => ({ value: a.id, label: `${dayjs(a.at).format('DD/MM/YYYY')} · ${a.service} · ${APPOINTMENT_STATUS[a.status].label}` }))} />
                        </div>
                        <div className="text-xs text-rose-600 flex items-center gap-1.5">
                            <AlertTriangle size={13} /> Tạo việc xử lý hạn {dayjs().add(SEVERITY[severity].slaHours, 'hour').format('HH:mm DD/MM')} ({SEVERITY[severity].slaHours} giờ). Quá hạn sẽ nhắc chuyển quản lý; đóng việc phải xác nhận khách hài lòng.
                        </div>
                    </div>
                )}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <DatePicker showTime={{ format: 'HH:mm' }} format="HH:mm DD/MM/YYYY" value={next} onChange={setNext}
                        disabled={result === 'complaint'}
                        placeholder={result === 'complaint' ? 'Đã có hạn xử lý khiếu nại' : result === 'follow' ? 'Hẹn chăm sóc tiếp (bắt buộc)' : 'Hẹn chăm sóc tiếp (tuỳ chọn)'}
                        status={result === 'follow' && !next ? 'warning' : undefined}
                        disabledDate={d => d.isBefore(dayjs(), 'day')} className="sm:w-64"
                        presets={[
                            { label: 'Ngày mai 9:00', value: dayjs().add(1, 'day').hour(9).minute(0).second(0) },
                            { label: 'Sau 3 ngày', value: dayjs().add(3, 'day').hour(9).minute(0).second(0) },
                            { label: 'Sau 1 tuần', value: dayjs().add(7, 'day').hour(9).minute(0).second(0) },
                        ]} />
                    <Button type="primary" icon={channel === 'care' ? <PenTool size={14} /> : <Send size={14} />} onClick={save} disabled={!text.trim() || needConsent}>Lưu chăm sóc</Button>
                </div>
            </div>

            <div className="overflow-x-auto mb-4">
                <Segmented value={filter} onChange={v => setFilter(v as TimelineFilter)} options={[
                    { value: 'all', label: `Tất cả (${counts.all})` },
                    { value: 'call', label: `Cuộc gọi (${counts.call})` },
                    { value: 'care', label: `Chăm sóc (${counts.care})` },
                    { value: 'appointment', label: `Lịch hẹn (${counts.appointment})` },
                    { value: 'survey', label: `Khảo sát (${counts.survey})` },
                    { value: 'other', label: `Khác (${counts.other})` },
                ]} />
            </div>

            {shown.length ? (
                <div className="relative pl-2 pb-4 before:absolute before:inset-y-0 before:left-[31px] before:w-[2px] before:bg-gray-200 dark:before:bg-gray-700">
                    {groupByDay(shown, t => t.at).map(g => (
                        <div key={g.day}>
                            <DayHeader label={g.day} />
                            {g.items.map(it => <TimelineEntry key={it.key} item={it} />)}
                        </div>
                    ))}
                </div>
            ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có hoạt động" />}
        </div>
    );
}

// ============================ Khảo sát CSAT ============================

function CsatTab({ customer, focusAppointment }: { customer: Customer; focusAppointment?: string }) {
    const s = useCallCenter();
    const all = s.surveys.filter(x => x.customerId === customer.id).sort((a, b) => b.at - a.at);
    const done = all.filter(x => (x.status || 'done') === 'done');
    const served = appointmentsOf(s, customer.id).filter(a => a.status === 'done');
    const states = new Map(served.map(a => [a.id, surveyStateOf(s, a.id)]));
    const pending = served.filter(a => states.get(a.id)?.state === 'pending');
    const [apptId, setApptId] = useState<string | undefined>(focusAppointment || pending[0]?.id);
    const [service, setService] = useState(0);
    const [attitude, setAttitude] = useState(0);
    const [pScore, setPScore] = useState(0);
    const [nps, setNps] = useState<number | null>(null);
    const [channel, setChannel] = useState<ContactChannel>('call');
    const [comment, setComment] = useState('');
    useEffect(() => { if (focusAppointment) setApptId(focusAppointment); }, [focusAppointment]);

    const ap = served.find(a => a.id === apptId);
    const avg = (xs: number[]) => (xs.length ? xs.reduce((t, v) => t + v, 0) / xs.length : 0);
    const npsScore = npsOf(done);
    const pScores = done.filter(x => x.practitionerScore).map(x => x.practitionerScore!);
    const lowNow = [service, attitude, ...(ap?.practitioner ? [pScore] : [])].every(Boolean) && Math.min(service, attitude, ap?.practitioner ? pScore : 5) <= CSAT_LOW;
    const detractor = nps !== null && nps <= NPS_DETRACTOR;
    // Xu hướng: lần khảo sát mới nhất so với lần trước
    const trend = done.length >= 2 ? (done[0].service + done[0].attitude) / 2 - (done[1].service + done[1].attitude) / 2 : null;

    const reset = () => { setService(0); setAttitude(0); setPScore(0); setNps(null); setComment(''); setApptId(pending.find(a => a.id !== apptId)?.id); };
    const save = () => {
        const err = callCenterApi.addSurvey({ customerId: customer.id, appointmentId: apptId, service, attitude, practitionerScore: ap?.practitioner ? pScore : undefined, nps, channel, comment, by: meName() });
        if (err) { message.warning(err); return; }
        message.success(lowNow ? 'Đã ghi nhận · tạo việc xử lý khiếu nại' : 'Đã ghi nhận khảo sát');
        reset();
    };
    const skip = (reason: 'declined' | 'unreachable') => {
        if (!apptId) return;
        const err = callCenterApi.skipSurvey({ customerId: customer.id, appointmentId: apptId, reason, note: comment, by: meName() });
        if (err) { message.warning(err); return; }
        const st = surveyStateOf(getCallCenter(), apptId);
        message.success(reason === 'declined' ? 'Đã ghi nhận khách từ chối khảo sát' : st.state === 'unreachable' ? `Không liên lạc được ${SURVEY_MAX_ATTEMPTS} lần — đã đóng khảo sát` : 'Đã ghi nhận · dời việc khảo sát sang 10:00 ngày mai');
        reset();
    };
    const complaintOf = (sv: Survey) => s.callbacks.filter(cb => cb.complaint?.surveyId === sv.id || (cb.complaint?.reopenedFrom && s.callbacks.find(x => x.id === cb.complaint!.reopenedFrom)?.complaint?.surveyId === sv.id));
    const stateLabel = (id: string) => {
        const st = states.get(id)!;
        return st.state === 'done' ? 'đã khảo sát' : st.state === 'declined' ? 'khách từ chối' : st.state === 'unreachable' ? 'không liên lạc được' : st.attempts ? `chưa khảo sát · đã thử ${st.attempts} lần` : 'chưa khảo sát';
    };

    return (
        <div className="space-y-5 max-w-3xl pb-6">
            {done.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        ['Số lượt khảo sát', String(done.length), trend === null ? '' : trend > 0 ? `↑ ${trend.toFixed(1)} so với lần trước` : trend < 0 ? `↓ ${Math.abs(trend).toFixed(1)} so với lần trước` : 'Không đổi'],
                        ['Dịch vụ / Thái độ', `${avg(done.map(x => x.service)).toFixed(1)} / ${avg(done.map(x => x.attitude)).toFixed(1)} ★`, ''],
                        ['Bác sĩ / KTV', pScores.length ? `${avg(pScores).toFixed(1)} ★` : '—', ''],
                        ['NPS', npsScore === null ? '—' : String(npsScore), 'Giới thiệu (9–10) − Không giới thiệu (0–6)'],
                    ].map(([l, v, hint]) => (
                        <div key={l} className="border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-center">
                            <div className="text-xs text-gray-500">{l}</div>
                            <div className="text-lg font-bold text-amber-500">{v}</div>
                            {hint && <div className={`text-[10px] ${hint.startsWith('↓') ? 'text-rose-500' : hint.startsWith('↑') ? 'text-emerald-600' : 'text-gray-400'}`}>{hint}</div>}
                        </div>
                    ))}
                </div>
            )}

            {!pending.length ? (
                <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-5 text-sm text-gray-500 text-center">
                    {served.length ? 'Mọi lần làm dịch vụ của khách đã được khảo sát hoặc đã đóng.' : 'Khách chưa làm dịch vụ lần nào — chỉ khảo sát sau khi lịch hẹn ở trạng thái "Đã thực hiện".'}
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-sm space-y-1">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-2">Ghi nhận khảo sát hài lòng</h3>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="text-gray-600 dark:text-gray-300 w-36">Lần sử dụng dịch vụ <span className="text-rose-500">*</span></span>
                        <Select className="min-w-[280px] flex-1" value={apptId} onChange={setApptId} placeholder="Chọn lần làm dịch vụ" status={!apptId ? 'warning' : undefined}
                            options={served.map(a => ({ value: a.id, disabled: states.get(a.id)?.state !== 'pending', label: `${dayjs(a.at).format('DD/MM/YYYY')} · ${a.service} (${stateLabel(a.id)})` }))} />
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-sm py-2">
                        <span className="text-gray-600 dark:text-gray-300 w-36">Hình thức</span>
                        <Radio.Group size="small" optionType="button" buttonStyle="solid" value={channel} onChange={e => setChannel(e.target.value)}
                            options={(Object.keys(CONTACT_CHANNEL) as ContactChannel[]).map(k => ({ value: k, label: CONTACT_CHANNEL[k] }))} />
                    </div>
                    {[
                        ['Chất lượng dịch vụ', service, setService],
                        ['Thái độ nhân viên', attitude, setAttitude],
                        ...(ap?.practitioner ? [[`Bác sĩ / KTV: ${ap.practitioner}`, pScore, setPScore]] : []),
                    ].map(([label, v, set]) => (
                        <div key={label as string} className="flex justify-between items-center py-2.5 border-b border-gray-100 dark:border-gray-800">
                            <span className="text-gray-700 dark:text-gray-200 text-sm">{label as string}</span>
                            <Rate value={v as number} onChange={set as (n: number) => void} />
                        </div>
                    ))}
                    <div className="py-2.5 border-b border-gray-100 dark:border-gray-800">
                        <div className="text-gray-700 dark:text-gray-200 text-sm mb-1.5">Khả năng giới thiệu cho người thân (0–10) <span className="text-gray-400 text-xs">tuỳ chọn</span></div>
                        <div className="flex flex-wrap gap-1">
                            {Array.from({ length: 11 }, (_, i) => (
                                <button key={i} type="button" onClick={() => setNps(nps === i ? null : i)}
                                    className={`w-8 h-8 rounded text-sm font-medium border cursor-pointer ${nps === i ? (i <= NPS_DETRACTOR ? 'bg-rose-500 border-rose-500 text-white' : i >= 9 ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-amber-400 border-amber-400 text-white') : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-amber-400'}`}>{i}</button>
                            ))}
                        </div>
                    </div>
                    <Input.TextArea className="!mt-3" value={comment} onChange={e => setComment(e.target.value)} autoSize={{ minRows: 2 }}
                        status={(lowNow || detractor) && !comment.trim() ? 'warning' : undefined}
                        placeholder={lowNow || detractor ? 'Bắt buộc: khách chưa hài lòng điều gì?' : 'Góp ý của khách (tuỳ chọn) — cũng dùng làm ghi chú khi khách từ chối / không liên lạc được'} />
                    {lowNow && <div className="text-xs text-rose-600 mt-2 flex items-center gap-1.5"><AlertTriangle size={13} /> Điểm ≤ {CSAT_LOW}★ sẽ tạo khiếu nại gắn với khảo sát này.</div>}
                    <div className="flex flex-wrap justify-between gap-2 mt-3">
                        <span className="flex gap-2">
                            <Popconfirm title="Khách từ chối khảo sát?" description="Lần làm dịch vụ này sẽ đóng khảo sát." okText="Xác nhận" cancelText="Huỷ" onConfirm={() => skip('declined')}>
                                <Button size="small" disabled={!apptId}>Khách từ chối</Button>
                            </Popconfirm>
                            <Button size="small" disabled={!apptId} onClick={() => skip('unreachable')}>
                                Không liên lạc được{apptId && states.get(apptId)?.attempts ? ` (${states.get(apptId)!.attempts}/${SURVEY_MAX_ATTEMPTS})` : ''}
                            </Button>
                        </span>
                        <Button type="primary" disabled={!apptId || !service || !attitude || (!!ap?.practitioner && !pScore) || ((lowNow || detractor) && !comment.trim())} onClick={save}>Lưu khảo sát</Button>
                    </div>
                </div>
            )}

            {all.map(sv => {
                const ap2 = served.find(a => a.id === sv.appointmentId);
                if (sv.status && sv.status !== 'done') {
                    return (
                        <div key={sv.id} className="border border-dashed border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2.5 text-sm text-gray-500 flex flex-wrap justify-between gap-2">
                            <span>{sv.status === 'declined' ? 'Khách từ chối khảo sát' : 'Không liên lạc được'}{ap2 ? ` · ${ap2.service} ${dayjs(ap2.at).format('DD/MM')}` : ''}{sv.comment ? ` — ${sv.comment}` : ''}</span>
                            <span className="text-xs text-gray-400">{sv.by} · {dayjs(sv.at).format('HH:mm DD/MM/YYYY')}</span>
                        </div>
                    );
                }
                const isLow = surveyMin(sv) <= CSAT_LOW;
                const complaints = complaintOf(sv);
                const openC = complaints.find(c => !c.doneAt);
                const lastC = complaints.sort((a, b) => (b.doneAt || 0) - (a.doneAt || 0))[0];
                return (
                    <div key={sv.id} className={`bg-white dark:bg-gray-900 border rounded-xl p-4 shadow-sm ${isLow ? 'border-rose-200' : 'border-gray-200 dark:border-gray-700'}`}>
                        <div className="flex justify-between text-xs text-gray-400 mb-2 gap-2">
                            <span>{sv.by}{ap2 ? ` · ${ap2.service} ${dayjs(ap2.at).format('DD/MM')}` : ''}{sv.channel ? ` · ${CONTACT_CHANNEL[sv.channel]}` : ''}</span>
                            <span>{dayjs(sv.at).format('HH:mm DD/MM/YYYY')}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-700 dark:text-gray-200 items-center">
                            <span>Dịch vụ: <Rate disabled value={sv.service} className="!text-sm" /></span>
                            <span>Thái độ: <Rate disabled value={sv.attitude} className="!text-sm" /></span>
                            {sv.practitionerScore ? <span>{ap2?.practitioner || 'BS/KTV'}: <Rate disabled value={sv.practitionerScore} className="!text-sm" /></span> : null}
                            {typeof sv.nps === 'number' && <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${sv.nps <= NPS_DETRACTOR ? 'bg-rose-50 text-rose-600' : sv.nps >= 9 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-700'}`}>NPS {sv.nps}</span>}
                        </div>
                        {sv.comment && <div className="text-sm text-gray-600 dark:text-gray-300 mt-2">{sv.comment}</div>}
                        {complaints.length > 0 && (
                            <div className={`text-xs mt-2 ${openC ? 'text-rose-600' : lastC?.satisfied ? 'text-emerald-600' : 'text-gray-500'}`}>
                                Khiếu nại: {openC ? `đang xử lý — ${assigneeOf(s, openC)}, hạn ${dayjs(openC.dueAt).format('HH:mm DD/MM')}` : lastC?.satisfied ? `đã xử lý, khách hài lòng (${lastC.doneBy})` : 'đã đóng'}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ============================ Hình ảnh trước / sau ============================

function ConsentBar({ customer }: { customer: Customer }) {
    const pc = customer.photoConsent;
    return (
        <div className={`rounded-xl border px-4 py-3 flex flex-wrap items-center gap-3 text-sm ${pc ? 'border-teal-200 bg-teal-50/50 dark:bg-teal-950/20' : 'border-amber-200 bg-amber-50/60 dark:bg-amber-950/20'}`}>
            <ShieldCheck size={16} className={pc ? 'text-teal-600' : 'text-amber-600'} />
            <div className="flex-1 min-w-[220px]">
                <div className="font-medium text-gray-800 dark:text-gray-100">Đồng ý chụp & sử dụng ảnh điều trị: {pc ? PHOTO_CONSENT_LABEL[pc.level] : <span className="text-amber-700">Chưa ghi nhận</span>}</div>
                <div className="text-xs text-gray-500">{pc ? `Ghi nhận bởi ${pc.by} · ${dayjs(pc.at).format('HH:mm DD/MM/YYYY')}` : 'Ảnh trước/sau là dữ liệu sức khoẻ (NĐ 13/2023) — cần khách đồng ý trước khi chụp/lưu.'}</div>
            </div>
            <Select size="small" className="w-56" value={pc?.level} placeholder="Ghi nhận sự đồng ý"
                options={(Object.keys(PHOTO_CONSENT_LABEL) as PhotoConsentLevel[]).map(k => ({ value: k, label: PHOTO_CONSENT_LABEL[k] }))}
                onChange={v => { callCenterApi.setPhotoConsent(customer.id, v, meName()); message.success('Đã ghi nhận sự đồng ý của khách'); }} />
            {pc && (
                <Popconfirm title="Khách thu hồi sự đồng ý?" description="Không tải thêm ảnh điều trị được nữa; ảnh cũ vẫn lưu hồ sơ nội bộ." okText="Thu hồi" cancelText="Huỷ" okButtonProps={{ danger: true }}
                    onConfirm={() => { callCenterApi.setPhotoConsent(customer.id, null, meName()); message.success('Đã ghi nhận thu hồi'); }}>
                    <Button size="small" danger type="text">Thu hồi</Button>
                </Popconfirm>
            )}
        </div>
    );
}

function StorageMeter() {
    useCallCenter();
    const used = storageBytes();
    const pct = Math.min(100, Math.round((used / STORAGE_LIMIT) * 100));
    return (
        <Tooltip title="Ảnh đang lưu tạm trong trình duyệt (chưa có API upload). Khi đầy sẽ không lưu thêm được.">
            <div className="flex items-center gap-2 text-xs text-gray-500 min-w-[220px]">
                <span className="shrink-0">Bộ nhớ tạm</span>
                <Progress percent={pct} size="small" status={pct >= 85 ? 'exception' : 'normal'} className="!m-0 flex-1" format={() => `${fmtBytes(used)} / ${fmtBytes(STORAGE_LIMIT)}`} />
            </div>
        </Tooltip>
    );
}

const PHOTO_DELETE_REASONS = ['Ảnh mờ / chụp lại', 'Tải nhầm khách', 'Trùng ảnh', 'Khách yêu cầu xoá'];

function GalleryTab({ customer, isManager }: { customer: Customer; isManager: boolean }) {
    const s = useCallCenter();
    const appts = appointmentsOf(s, customer.id).filter(a => a.status !== 'cancelled');
    const me = meName();
    const [tag, setTag] = useState<PhotoTag>('before');
    const [apptId, setApptId] = useState<string | undefined>(appts[0]?.id);
    const [angle, setAngle] = useState<PhotoAngle>('front');
    const [milestone, setMilestone] = useState<PhotoMilestone | 'auto'>('auto');
    const [caption, setCaption] = useState('');
    const [filter, setFilter] = useState<PhotoTag | 'all' | 'deleted'>('all');
    const [busy, setBusy] = useState(false);
    const [deleting, setDeleting] = useState<Photo | null>(null);
    const [reason, setReason] = useState('');
    const fileRef = useRef<HTMLInputElement>(null);

    const apptOptions = tag === 'after' ? appts.filter(a => a.status === 'done') : appts;
    const pickTag = (t: PhotoTag) => {
        setTag(t);
        if (t === 'after' && !appts.some(a => a.id === apptId && a.status === 'done')) setApptId(appts.find(a => a.status === 'done')?.id);
    };
    const canDelete = (p: Photo) => !p.by || p.by === me || isManager;
    const consent = customer.photoConsent;
    // Ảnh điều trị chưa được khách cho phép dùng truyền thông thì không cho tải xuống
    const canDownload = (p: Photo) => !isMedicalPhoto(p.tag) || consent?.level === 'marketing';
    const photos = visiblePhotos(customer).sort((a, b) => b.at - a.at);
    const deleted = customer.photos.filter(p => p.deleted);
    const shown = filter === 'deleted' ? deleted : filter === 'all' ? photos : photos.filter(p => (p.tag || 'other') === filter);
    const selAppt = appts.find(a => a.id === apptId);
    const autoMilestone = selAppt ? milestoneFor(dayjs().diff(dayjs(selAppt.at), 'day')) : 'd0';

    // So sánh theo từng lần làm DV × góc chụp: Trước → các mốc sau
    const compare = appts.map(a => {
        const of = (p: Photo) => p.appointmentId === a.id;
        const angles = (Object.keys(PHOTO_ANGLE_LABEL) as PhotoAngle[]).map(ag => {
            const at = (p: Photo) => of(p) && (p.angle || 'front') === ag;
            const before = photos.find(p => at(p) && p.tag === 'before');
            const after = (Object.keys(PHOTO_MILESTONE) as PhotoMilestone[]).map(m => ({ m, p: photos.find(p => at(p) && p.tag === 'after' && (p.milestone || 'd0') === m) })).filter(x => x.p);
            return { ag, before, after };
        }).filter(x => x.before && x.after.length);
        return { a, angles };
    }).filter(x => x.angles.length);

    const medicalBlocked = isMedicalPhoto(tag) && !consent;
    const afterBlocked = tag === 'after' && !apptOptions.some(a => a.id === apptId);
    const upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/') && f.size <= 10 * 1024 * 1024);
        e.target.value = '';
        if (!files.length) { message.warning('Chọn ảnh (≤10MB)'); return; }
        setBusy(true);
        try {
            const urls = await Promise.all(files.map(f => fileToDataUrl(f)));
            const err = callCenterApi.addPhotos(customer.id, urls, {
                tag, appointmentId: apptId, by: me, caption, ...(isMedicalPhoto(tag) ? { angle } : {}), ...(tag === 'after' && milestone !== 'auto' ? { milestone } : {}),
            });
            if (err) message.error(err); else { message.success(`Đã thêm ${urls.length} ảnh "${PHOTO_TAG_LABEL[tag]}"`); setCaption(''); }
        } finally { setBusy(false); }
    };
    const confirmDelete = () => {
        if (!deleting) return;
        const err = callCenterApi.removePhoto(customer.id, deleting.id, me, reason);
        if (err) { message.warning(err); return; }
        message.success('Đã xoá ảnh (lưu vết trong hồ sơ)');
        setDeleting(null);
    };

    return (
        <div className="space-y-5 pb-6">
            <ConsentBar customer={customer} />

            <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                    <Radio.Group size="small" optionType="button" buttonStyle="solid" value={tag} onChange={e => pickTag(e.target.value)}
                        options={(Object.keys(PHOTO_TAG_LABEL) as PhotoTag[]).map(k => ({ value: k, label: PHOTO_TAG_LABEL[k] }))} />
                    <Select allowClear={tag !== 'after'} size="small" className="min-w-[240px]" value={apptId} onChange={setApptId}
                        placeholder={tag === 'after' ? 'Chọn lần làm dịch vụ (bắt buộc)' : 'Gắn với lần làm dịch vụ'}
                        notFoundContent={tag === 'after' ? 'Chưa có lần làm dịch vụ đã thực hiện' : undefined}
                        options={apptOptions.map(a => ({ value: a.id, label: `${dayjs(a.at).format('DD/MM/YYYY')} · ${a.service} · ${APPOINTMENT_STATUS[a.status].label}` }))} />
                    <StorageMeter />
                </div>
                {isMedicalPhoto(tag) && (
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span className="text-gray-500">Góc chụp</span>
                        <Select size="small" className="w-36" value={angle} onChange={setAngle} options={(Object.keys(PHOTO_ANGLE_LABEL) as PhotoAngle[]).map(k => ({ value: k, label: PHOTO_ANGLE_LABEL[k] }))} />
                        {tag === 'after' && (
                            <>
                                <span className="text-gray-500">Mốc</span>
                                <Select size="small" className="w-52" value={milestone} onChange={setMilestone}
                                    options={[{ value: 'auto', label: `Tự tính (${PHOTO_MILESTONE[autoMilestone].label})` }, ...(Object.keys(PHOTO_MILESTONE) as PhotoMilestone[]).map(k => ({ value: k, label: PHOTO_MILESTONE[k].label }))]} />
                            </>
                        )}
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-3">
                    <Input size="small" className="!max-w-sm" value={caption} onChange={e => setCaption(e.target.value)} placeholder="Ghi chú ảnh (tuỳ chọn), VD: vùng trán, sau 2 tuần..." />
                    <Tooltip title={medicalBlocked ? 'Cần ghi nhận khách đồng ý chụp & lưu ảnh điều trị' : afterBlocked ? 'Ảnh "Sau điều trị" phải gắn với lần làm dịch vụ đã thực hiện' : undefined}>
                        <Button icon={<ImagePlus size={14} />} loading={busy} disabled={medicalBlocked || afterBlocked} onClick={() => fileRef.current?.click()}>Tải ảnh lên</Button>
                    </Tooltip>
                    <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} />
                    <span className="text-xs text-gray-400">Chỉ người tải lên (hoặc quản lý) được xoá · xoá có lưu vết</span>
                </div>
            </div>

            {compare.length > 0 && (
                <div>
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">So sánh trước / sau (cùng góc chụp)</div>
                    <div className="space-y-3">
                        {compare.map(({ a, angles }) => (
                            <div key={a.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-3">
                                <div className="text-xs text-gray-500 mb-2">{a.service} · {dayjs(a.at).format('DD/MM/YYYY')}{a.practitioner ? ` · ${a.practitioner}` : ''}</div>
                                <Image.PreviewGroup>
                                    {angles.map(({ ag, before, after }) => (
                                        <div key={ag} className="mb-2 last:mb-0">
                                            <div className="text-[11px] font-medium text-gray-500 mb-1">{PHOTO_ANGLE_LABEL[ag]}</div>
                                            <div className="flex gap-2 overflow-x-auto">
                                                {[{ label: 'Trước', p: before! }, ...after.map(x => ({ label: PHOTO_MILESTONE[x.m].label, p: x.p! }))].map(cell => (
                                                    <div key={cell.p.id} className="shrink-0 w-36">
                                                        <div className="text-[11px] text-gray-400 mb-0.5">{cell.label}</div>
                                                        <Image src={cell.p.url} className="rounded-lg object-cover !h-28 !w-36" />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </Image.PreviewGroup>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="overflow-x-auto">
                <Segmented value={filter} onChange={v => setFilter(v as typeof filter)} options={[
                    { value: 'all', label: `Tất cả (${photos.length})` },
                    ...(Object.keys(PHOTO_TAG_LABEL) as PhotoTag[]).map(k => ({ value: k, label: `${PHOTO_TAG_LABEL[k]} (${photos.filter(p => (p.tag || 'other') === k).length})` })),
                    ...(deleted.length ? [{ value: 'deleted', label: `Đã xoá (${deleted.length})` }] : []),
                ]} />
            </div>
            {shown.length ? (
                <Image.PreviewGroup>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {shown.map(p => {
                            const ap = appts.find(a => a.id === p.appointmentId);
                            return (
                                <div key={p.id} className={`relative group border rounded-lg overflow-hidden ${p.deleted ? 'border-rose-200 opacity-70' : 'border-gray-200 dark:border-gray-700'}`}>
                                    <Image src={p.url} className="object-cover !h-32 !w-full" />
                                    <div className="px-2 py-1.5 text-[11px] text-gray-500 leading-tight space-y-0.5">
                                        <div className="font-medium text-gray-700 dark:text-gray-200">
                                            {PHOTO_TAG_LABEL[p.tag || 'other']}{p.milestone ? ` · ${PHOTO_MILESTONE[p.milestone].label}` : ''}{p.angle ? ` · ${PHOTO_ANGLE_LABEL[p.angle]}` : ''}
                                        </div>
                                        {p.caption && <div className="truncate text-gray-600 dark:text-gray-300" title={p.caption}>{p.caption}</div>}
                                        <div className="truncate">{ap ? `${ap.service} · ` : ''}{dayjs(p.at).format('DD/MM/YYYY')}{p.by ? ` · ${p.by}` : ''}</div>
                                        {p.deleted && <div className="text-rose-500 truncate" title={p.deleted.reason}>Xoá bởi {p.deleted.by}: {p.deleted.reason}</div>}
                                    </div>
                                    <div className="absolute top-1.5 right-1.5 hidden group-hover:flex gap-1">
                                        {!p.deleted && canDownload(p) && (
                                            <a href={p.url} download={`${customer.name}-${p.tag || 'anh'}-${dayjs(p.at).format('YYYYMMDD')}.jpg`} className="w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center" title="Tải xuống"><Download size={12} /></a>
                                        )}
                                        {!p.deleted && canDelete(p) && (
                                            <button type="button" className="w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center cursor-pointer" title="Xoá ảnh" onClick={() => { setReason(''); setDeleting(p); }}><Trash2 size={12} /></button>
                                        )}
                                        {p.deleted && isManager && (
                                            <button type="button" className="w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center cursor-pointer" title="Khôi phục" onClick={() => { callCenterApi.restorePhoto(customer.id, p.id, me); message.success('Đã khôi phục ảnh'); }}><RotateCcw size={12} /></button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Image.PreviewGroup>
            ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có hình ảnh" />}
            {isMedicalPhoto('before') && consent?.level !== 'marketing' && photos.some(p => isMedicalPhoto(p.tag)) && (
                <div className="text-xs text-gray-400">Ảnh điều trị chỉ tải xuống được khi khách đồng ý "{PHOTO_CONSENT_LABEL.marketing}".</div>
            )}

            <Modal open={!!deleting} title="Xoá ảnh" onCancel={() => setDeleting(null)} width={440}
                footer={[<Button key="c" onClick={() => setDeleting(null)}>Huỷ</Button>, <Button key="s" danger type="primary" disabled={!reason.trim()} onClick={confirmDelete}>Xoá ảnh</Button>]}>
                <div className="space-y-2 text-sm">
                    <div className="text-gray-500">Ảnh được ẩn khỏi hồ sơ nhưng vẫn lưu vết (quản lý có thể khôi phục). Lý do <span className="text-rose-500">*</span></div>
                    <Input.TextArea value={reason} onChange={e => setReason(e.target.value)} autoSize={{ minRows: 2 }} placeholder="Lý do xoá" />
                    <div className="flex flex-wrap gap-1.5">{PHOTO_DELETE_REASONS.map(r => <Chip key={r} text={r} onClick={() => setReason(r)} />)}</div>
                </div>
            </Modal>
        </div>
    );
}

export { CareJourney, CsatTab, GalleryTab };
